import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase, readableError } from '../../lib/supabase'
import { useSession } from '../../lib/session'
import { Shell } from '../../components/Shell'
import { Spinner } from '../../components/ui'

/**
 * The "+ Song" tab. Creates the draft and steps straight into it, so the tab
 * behaves like an action rather than a screen.
 *
 * No song_number here. A reference is issued by submit_song() when the song is
 * actually sent (migration 31) — stamping one at creation gave every abandoned
 * draft a permanent-looking reference that identified nothing.
 *
 * apra_amcos_member starts null for everyone, including accounts that predate
 * the question. A song can't be created until it's answered — the publisher
 * document generated at finish_song() needs it on record, so it has to exist
 * before the song does, not be back-filled after.
 */
export default function NewSong() {
  const { user, profile } = useSession()
  const nav = useNavigate()
  const once = useRef(false)
  const [err, setErr] = useState(null)

  useEffect(() => {
    if (once.current) return // StrictMode double-mount would create two drafts
    if (profile && profile.apra_amcos_member === null) {
      once.current = true
      nav('/rights', { replace: true, state: { from: '/songs/new' } })
      return
    }
    if (!profile) return // wait for the profile to load before deciding
    once.current = true

    ;(async () => {
      const { data: song, error } = await supabase.from('songs')
        .insert({ songwriter_id: user.id, title: 'Untitled', status: 'awaiting_cosign' })
        .select().single()
      if (error) return setErr(readableError(error))

      // The writer is a co-writer too — their own row holds their split and signature.
      const me = [profile?.first_name, profile?.last_name].filter(Boolean).join(' ') || user.email
      await supabase.from('song_cowriters').insert({
        song_id: song.id, cowriter_name: me, cowriter_email: user.email,
        user_id: user.id, split_percentage: 100, is_primary: true,
      })
      nav(`/songs/${song.id}/1`, { replace: true })
    })()
  }, [user, profile, nav])

  if (err) return (
    <Shell tabs hidePlayer>
      <h2 className="q">That didn't start.</h2>
      <p className="sub">{err}</p>
      <button className="btn btn-quiet" onClick={() => nav('/me')}>Back to your songs</button>
    </Shell>
  )

  return <Shell tabs hidePlayer><Spinner /></Shell>
}
